import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Editor from '../components/CodeEditor';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import mermaid from 'mermaid';
import { getErrorMessage, getErrorStatus, lessonManagementService, lessonService, simulationService, SimulationTimeUsedUp, submissionService } from '../services/api';
import PaymentsNote from '../components/PaymentsNote';
import UpgradeButton from '../components/UpgradeButton';
import type { Lesson, LessonAssignment, Submission } from '../types/api';
import MetricsDisplay from '../components/MetricsDisplay';
import AppNavBar from '../components/AppNavBar';
import InfoChip from '../components/InfoChip';
import ReadinessBadge from '../components/ReadinessBadge';
import RunResults from '../components/RunResults';
import ReportIssueDialog, { type IssueReportContext } from '../components/ReportIssueDialog';
import StatusBadge from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { describeSubmissionWindow, formatDailyTime, formatDateTime, formatMinutes, formatResetTime } from '../utils/format';
import {
  Send,
  History,
  BookOpen,
  Code,
  Cpu,
  AppWindow,
  Trash2,
  Copy,
  Play,
  X,
  RefreshCw,
  RotateCcw,
  GraduationCap,
  AlertTriangle,
  CalendarClock,
  Clock,
  Flag,
} from 'lucide-react';
import 'katex/dist/katex.min.css';
import { EmptyState, LoadingState, Spinner } from '../components/ui';

// Mermaid component
const Mermaid: React.FC<{ chart: string; isDarkMode: boolean }> = ({ chart, isDarkMode }) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!chart) {
      return;
    }
    let cancelled = false;
    // 'strict' sanitizes labels and disables click callbacks: the README comes from a teacher's repository and
    // the SVG is injected as HTML below.
    mermaid.initialize({
      startOnLoad: false,
      theme: isDarkMode ? 'dark' : 'default',
      securityLevel: 'strict'
    });

    const id = `mermaid-${Math.random().toString(36).slice(2, 11)}`;
    mermaid.render(id, chart)
      .then(({ svg }) => {
        if (!cancelled) {
          setSvg(svg);
          setError('');
        }
      })
      .catch((renderError) => {
        console.error('Mermaid render error:', renderError);
        // Mermaid can leave its scratch element behind on a parse error.
        document.getElementById(`d${id}`)?.remove();
        if (!cancelled) {
          setSvg('');
          setError(renderError?.message || String(renderError));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chart, isDarkMode]);

  if (error) {
    // Rendered as text: the message quotes the diagram source.
    return <div className="text-red-600 dark:text-red-400 p-2 text-sm whitespace-pre-wrap">Error rendering diagram: {error}</div>;
  }

  return <div dangerouslySetInnerHTML={{ __html: svg }} />;
};

// react-markdown's default drops javascript: and other unsafe URLs. Lesson sync inlines README images as
// data: URIs, so those are let through for images only.
const lessonUrlTransform = (url: string, key: string) =>
  key === 'src' && url.startsWith('data:image/') ? url : defaultUrlTransform(url);

type WebotsInstanceDetails = {
  webotsInstanceUrl: string;
  simulationServerUrl: string;
  webotsInstancePort: string;
  // Sent by the simulation server when the session starts; goes with every request to it for this session.
  sessionKey: string | null;
};

// Query string for the simulation server's per-session endpoints (metrics, visualization-*).
const sessionQuery = (details: WebotsInstanceDetails) =>
  `port=${encodeURIComponent(details.webotsInstancePort)}${details.sessionKey ? `&key=${encodeURIComponent(details.sessionKey)}` : ''}`;

type SimulationStatus = 'connecting' | 'ready' | 'error' | 'time-used-up';
type DraftStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
// The lesson page's panes (docs/REQUIREMENTS.md WRK-1). On wide screens the simulation is always on the right and the
// others are tabs on the left; on narrow ones all four are tabs.
type Pane = 'theory' | 'code' | 'visualization' | 'simulation';
type LeftPane = Exclude<Pane, 'simulation'>;
const WIDE_LAYOUT = '(min-width: 1024px)'; // Tailwind's lg, where the columns start

// Autosave the student's draft this long after they stop typing.
const DRAFT_AUTOSAVE_DELAY_MS = 2000;

const LessonPage: React.FC = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const toast = useToast();
  const confirm = useConfirm();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isStudent = user?.role === 'student';
  // The student dashboard links class lessons with ?class=<id> and the library's lessons (practice) with ?demo=1.
  const classIdParam = Number(searchParams.get('class')) || null;
  const isDemo = searchParams.get('demo') === '1';
  const routeInitialCode = (location.state as { initialCode?: string } | null)?.initialCode;
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loadError, setLoadError] = useState('');
  const [assignment, setAssignment] = useState<LessonAssignment | null>(null);
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  // What the simulation pill shows besides the connection and the time left: a save's result, for a few seconds.
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!output || output === 'Loading simulation...' || output === 'Simulation connected and ready!') {
      setNotice('');
      return;
    }
    setNotice(output);
    const timeoutId = window.setTimeout(() => setNotice(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [output]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  // "Report an issue" (WRK-21). While its dialog is open the page's shortcuts stay out of the way (Ctrl+Enter in its
  // text box must not run the code); closing it returns focus to its button.
  const [showReport, setShowReport] = useState(false);
  const showReportRef = useRef(false);
  showReportRef.current = showReport;
  const reportButtonRef = useRef<HTMLButtonElement>(null);
  const [webotsInstanceDetails, setWebotsInstanceDetails] = useState<WebotsInstanceDetails | null>(null);
  const [simulationStatus, setSimulationStatus] = useState<SimulationStatus>('connecting');
  // Today's simulation time (free accounts can try, not use: docs/BUSINESS.md); null until known or when not limited.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [timeResetsAt, setTimeResetsAt] = useState<string | null>(null);
  const [timeUsedUp, setTimeUsedUp] = useState<{ source: 'personal' | 'class'; plan?: string; limitSeconds?: number | null }>({ source: 'personal' });
  // Whose time the pill shows (SIM-17): a paid class's class time for its lessons opened from it (PLAN-3), else the
  // user's own.
  const [timeSource, setTimeSource] = useState<'personal' | 'class'>('personal');
  const refreshAllowance = useCallback(() => {
    simulationService.allowance(Number(lessonId) || undefined, classIdParam).then((allowance) => {
      setSecondsLeft(allowance.enforced && !allowance.unlimited ? allowance.remaining_seconds : null);
      setTimeResetsAt(allowance.resets_at);
      setTimeSource(allowance.source === 'class' ? 'class' : 'personal');
    }).catch(() => setSecondsLeft(null));
  }, [lessonId, classIdParam]);
  // Only the time the simulation plays counts (SIM-8), and the simulation server reports it every 30 s while it plays:
  // while one is connected, follow those reports.
  useEffect(() => {
    refreshAllowance();
    if (simulationStatus !== 'ready') return;
    const intervalId = window.setInterval(refreshAllowance, 30000);
    return () => window.clearInterval(intervalId);
  }, [refreshAllowance, simulationStatus]);
  const [simulationError, setSimulationError] = useState('');
  const [viewerKey, setViewerKey] = useState(0);
  const [iframeReadyCount, setIframeReadyCount] = useState(0);
  const [draftStatus, setDraftStatus] = useState<DraftStatus>('idle');
  const [pane, setPane] = useState<Pane>('theory');
  // The left column's pane, kept while a phone shows the simulation (and shown when the screen widens).
  const [leftPane, setLeftPane] = useState<LeftPane>('theory');
  // The Code tab is highlighted until the student opens it: that's where the work is (WRK-1).
  const [codeOpened, setCodeOpened] = useState(false);
  const [isWide, setIsWide] = useState(() => window.matchMedia(WIDE_LAYOUT).matches);
  const [visualizationAvailable, setVisualizationAvailable] = useState(false);
  const [visualizationUrl, setVisualizationUrl] = useState('');
  // A new visualization (Python windows from the student's code) marks its tab until it is opened.
  const [visualizationUnseen, setVisualizationUnseen] = useState(false);
  const [editorSessionKey, setEditorSessionKey] = useState(0);
  const visualizationStatusEndpointRef = useRef('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Latest editor content, the content last stored as the draft, and the lesson they belong to: read by the
  // autosave timer, the unload warning and the flush when leaving the lesson, which outlive a render.
  const codeRef = useRef('');
  const savedCodeRef = useRef('');
  const draftLessonIdRef = useRef<string | null>(null);
  // Whether the student pushed their code to the simulation at least once (Save & Run) in this lesson.
  const hasRunCodeRef = useRef(false);
  // The last finished run's metrics (WRK-20), for everyone, with or without a class: a lesson's supervisor writes
  // metrics.json and pauses the world when a run ends. A run counts once the simulation played since the last results;
  // a new run hides them. metrics.json stays from the previous run, so it is read only on that pause.
  const [runMetrics, setRunMetrics] = useState<Record<string, unknown> | null>(null);
  const [runEndedCount, setRunEndedCount] = useState(0);
  const runPlayingRef = useRef(false);
  const webotsInstanceDetailsRef = useRef<WebotsInstanceDetails | null>(null);
  webotsInstanceDetailsRef.current = webotsInstanceDetails;
  const submissionsRequestRef = useRef(0);
  const monacoTheme = isDarkMode ? 'vs-dark' : 'vs';
  const submissionWindow = describeSubmissionWindow(assignment);
  const canSubmit = isStudent && !isDemo;

  useEffect(() => {
    document.documentElement.classList.add('lesson-page-locked');
    document.body.classList.add('lesson-page-locked');

    return () => {
      document.documentElement.classList.remove('lesson-page-locked');
      document.body.classList.remove('lesson-page-locked');
    };
  }, []);

  const postToViewer = useCallback((message: Record<string, unknown>) => {
    // The viewer is served by this app, so its origin is ours; never '*' (the message can carry a token).
    iframeRef.current?.contentWindow?.postMessage(message, window.location.origin);
  }, []);

  // Messages from the Webots viewer iframe (public/webots-viewer.html).
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }
      const data = event.data;
      if (!data || typeof data !== 'object') {
        return;
      }
      if (data.type === 'webots-ready') {
        setSimulationStatus('ready');
        setSimulationError('');
        setOutput('Simulation connected and ready!');
      } else if (data.type === 'instance-details') {
        setWebotsInstanceDetails({
          webotsInstanceUrl: String(data.webotsInstanceUrl || ''),
          simulationServerUrl: String(data.simulationServerUrl || ''),
          webotsInstancePort: String(data.webotsInstancePort || ''),
          sessionKey: typeof data.sessionKey === 'string' && data.sessionKey ? data.sessionKey : null,
        });
      } else if (data.type === 'iframe-ready') {
        setIframeReadyCount((count) => count + 1);
      } else if (data.type === 'webots-error') {
        setSimulationStatus('error');
        setSimulationError(typeof data.message === 'string' ? data.message : 'The simulation could not start.');
        setWebotsInstanceDetails(null);
      } else if (data.type === 'webots-notice' && typeof data.message === 'string') {
        toast.info(data.message, { durationMs: 10000 });
      } else if (data.type === 'simulation-state') {
        if (data.state === 'running') {
          runPlayingRef.current = true;
          setRunMetrics(null);
        } else if (data.state === 'paused' && runPlayingRef.current) {
          runPlayingRef.current = false;
          setRunEndedCount((count) => count + 1);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toast]);

  const saveDraft = useCallback(async (): Promise<boolean> => {
    const draftLessonId = draftLessonIdRef.current;
    const codeToSave = codeRef.current;
    if (!isStudent || !draftLessonId || codeToSave === savedCodeRef.current) {
      return true;
    }
    setDraftStatus('saving');
    try {
      await submissionService.saveDraft(draftLessonId, codeToSave);
      if (draftLessonIdRef.current === draftLessonId) {
        savedCodeRef.current = codeToSave;
        setDraftStatus(codeRef.current === codeToSave ? 'saved' : 'dirty');
      }
      return true;
    } catch (error) {
      console.error('Error saving draft:', error);
      if (draftLessonIdRef.current === draftLessonId) {
        setDraftStatus('error');
      }
      return false;
    }
  }, [isStudent]);

  // Leaving the lesson (another lesson or another page): store what the autosave timer hasn't stored yet.
  useEffect(() => {
    return () => {
      const draftLessonId = draftLessonIdRef.current;
      const pendingCode = codeRef.current;
      if (isStudent && draftLessonId && pendingCode !== savedCodeRef.current) {
        savedCodeRef.current = pendingCode;
        submissionService.saveDraft(draftLessonId, pendingCode).catch((error) => console.error('Error saving draft:', error));
      }
    };
  }, [lessonId, isStudent]);

  // Load the lesson (and the student's draft) whenever the lesson or the navigation entry changes.
  useEffect(() => {
    let cancelled = false;
    setLesson(null);
    setLoadError('');
    setAssignment(null);
    setWebotsInstanceDetails(null);
    setSimulationStatus('connecting');
    setSimulationError('');
    setDraftStatus('idle');
    setVisualizationAvailable(false);
    setVisualizationUrl('');
    setVisualizationUnseen(false);
    visualizationStatusEndpointRef.current = '';
    draftLessonIdRef.current = null;
    hasRunCodeRef.current = false;

    const load = async () => {
      if (!lessonId) {
        return;
      }
      try {
        const lessonData = await lessonService.get(lessonId);
        let initialCode = routeInitialCode;

        if (initialCode == null && isStudent) {
          try {
            initialCode = (await submissionService.getDraft(lessonId)).code;
          } catch (error) {
            if (getErrorStatus(error) !== 404) {
              console.error('Error loading lesson draft:', error);
            }
          }
        }

        if (cancelled) {
          return;
        }
        // Use initial code from route state if provided (e.g., teacher running a student's submission)
        const startingCode = initialCode ?? lessonData.starter_code ?? '';
        codeRef.current = startingCode;
        savedCodeRef.current = startingCode;
        draftLessonIdRef.current = lessonId;
        setCode(startingCode);
        setLesson(lessonData);
        const firstPane: LeftPane = lessonData.theory_content ? 'theory' : 'code';
        setPane(firstPane);
        setLeftPane(firstPane);
        setCodeOpened(firstPane === 'code');
        // Remount Monaco when a new lesson payload is loaded so it starts from the
        // latest lesson/submission content without treating the editor as controlled.
        setEditorSessionKey((currentKey) => currentKey + 1);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading lesson:', error);
          setLoadError(getErrorStatus(error) === 404
            ? "This lesson doesn't exist or isn't available to you."
            : getErrorMessage(error, 'The lesson could not be loaded.'));
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // routeInitialCode belongs to the navigation entry (location.key)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, location.key, isStudent]);

  const loadSubmissions = useCallback(async () => {
    const requestId = ++submissionsRequestRef.current;
    if (!lessonId || !canSubmit) {
      setSubmissions([]);
      return;
    }
    try {
      const data = await submissionService.mine(lessonId);
      // Only the latest request counts: a late answer may be for the lesson the student already left.
      if (requestId === submissionsRequestRef.current) {
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  }, [lessonId, canSubmit]);

  useEffect(() => {
    setSubmissions([]);
    loadSubmissions();
  }, [loadSubmissions]);

  // The class assignment (deadline, submissions on/off), when the dashboard told us the class.
  useEffect(() => {
    if (!classIdParam || !lessonId || !isStudent) {
      return;
    }
    let cancelled = false;
    lessonManagementService.getClassLessons(classIdParam)
      .then((assignments) => {
        if (!cancelled) {
          setAssignment(assignments.find((item) => item.lesson_id === Number(lessonId)) ?? null);
        }
      })
      .catch((error) => console.error('Error loading the class assignment:', error));
    return () => {
      cancelled = true;
    };
  }, [classIdParam, lessonId, isStudent]);

  // Autosave the draft a moment after typing stops.
  useEffect(() => {
    if (!isStudent || !lesson || code === savedCodeRef.current) {
      return;
    }
    setDraftStatus('dirty');
    const timeoutId = window.setTimeout(() => { saveDraft(); }, DRAFT_AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [code, lesson, isStudent, saveDraft]);

  // Closing or reloading the tab with a draft not yet stored: let the browser ask.
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (isStudent && draftLessonIdRef.current && codeRef.current !== savedCodeRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isStudent]);

  // Start the lesson's world each time the viewer iframe announces readiness. The simulation token, when the
  // backend issues one, is short-lived, so it is requested right before every start.
  useEffect(() => {
    if (!lesson || iframeReadyCount === 0) {
      return;
    }
    let cancelled = false;
    const start = async () => {
      let worldFile = lesson.world_file;
      try {
        const token = await lessonService.simulationToken(lesson.id, classIdParam);
        if (token) {
          worldFile = `${lesson.world_file}?webots_token=${encodeURIComponent(token)}`;
        }
      } catch (error) {
        if (error instanceof SimulationTimeUsedUp) {
          if (!cancelled) {
            setTimeUsedUp({ source: error.source, plan: error.plan, limitSeconds: error.limitSeconds });
            setTimeResetsAt(error.resetsAt);
            setSimulationStatus('time-used-up');
            setOutput('');
          }
          return;
        }
        // An older backend without the endpoint: start without a token, as before.
        console.warn('No simulation token:', error);
      }
      refreshAllowance();
      if (cancelled) {
        return;
      }
      setSimulationStatus('connecting');
      setSimulationError('');
      postToViewer({ type: 'load-world', worldFile });
      setOutput('Loading simulation...');
    };
    start();
    return () => {
      cancelled = true;
    };
  }, [lesson, iframeReadyCount, postToViewer, refreshAllowance, classIdParam]);

  // The viewer redraws for its frame's size: when it is shown again (a phone's Simulation tab) and whenever the frame
  // changes size (the browser's bars, rotation, a resized window); not every browser tells the frame itself.
  useEffect(() => {
    const notifyResize = () => {
      postToViewer({ type: 'resize-view' });
    };

    notifyResize();
    const timeoutId = window.setTimeout(notifyResize, 150);
    const iframe = iframeRef.current;
    const observer = iframe && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(notifyResize) : null;
    if (iframe) observer?.observe(iframe);
    return () => {
      window.clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [pane, postToViewer, viewerKey]);

  // Wide screens show the simulation beside the tabs: a phone's Simulation tab becomes the left pane it came from.
  useEffect(() => {
    const query = window.matchMedia(WIDE_LAYOUT);
    const onChange = () => setIsWide(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  useEffect(() => {
    if (isWide && pane === 'simulation') setPane(leftPane);
  }, [isWide, pane, leftPane]);

  const selectPane = (next: Pane) => {
    setPane(next);
    if (next !== 'simulation') setLeftPane(next);
    if (next === 'visualization') setVisualizationUnseen(false);
    if (next === 'code') setCodeOpened(true);
  };

  // A run ended (the world paused after playing): its metrics, when the lesson's supervisor wrote them (WRK-20).
  useEffect(() => {
    const details = webotsInstanceDetailsRef.current;
    if (!runEndedCount || !details?.simulationServerUrl) return;
    let cancelled = false;
    fetch(`${details.simulationServerUrl.replace(/\/$/, '')}/metrics?${sessionQuery(details)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((metrics) => {
        if (!cancelled && metrics && typeof metrics === 'object' && !Array.isArray(metrics) && Object.keys(metrics).length > 0) {
          setRunMetrics(metrics);
        }
      })
      .catch(() => { /* no metrics: the lesson has no supervisor, or the run didn't finish */ });
    return () => { cancelled = true; };
  }, [runEndedCount]);

  // After Save & Run, show what the code does (WRK-8): on a phone the simulation; on a wide screen, where the simulation
  // is always visible, the Python windows when there are any (the effect below shows them when they appear later).
  const showResultsAfterRun = () => {
    if (!isWide) selectPane('simulation');
    else if (visualizationAvailable) selectPane('visualization');
  };
  // On a wide screen, Python windows that appear (after Save & Run, or when a lesson's own code opens them on play)
  // take the left column: the simulation stays visible on the right. On a phone the tab gets its dot instead of
  // pulling the student away from what they watch (WRK-15).
  useEffect(() => {
    if (visualizationAvailable && isWide) {
      setPane('visualization');
      setLeftPane('visualization');
      setVisualizationUnseen(false);
    }
  }, [visualizationAvailable, isWide]);

  // The visualization tab exists only while there are windows: when they close, its tab goes back to the code.
  useEffect(() => {
    if (visualizationAvailable) return;
    if (leftPane === 'visualization') setLeftPane('code');
    if (pane === 'visualization') setPane('code');
  }, [visualizationAvailable, pane, leftPane]);

  useEffect(() => {
    if (!webotsInstanceDetails?.simulationServerUrl || !webotsInstanceDetails.webotsInstancePort) {
      setVisualizationAvailable(false);
      setVisualizationUrl('');
      visualizationStatusEndpointRef.current = '';
      return;
    }

    let cancelled = false;
    const buildStatusCandidates = () => {
      const candidates: string[] = [];
      const seen = new Set<string>();
      const pushUnique = (url: string) => {
        if (!seen.has(url)) {
          seen.add(url);
          candidates.push(url);
        }
      };

      const path = `/visualization-status?${sessionQuery(webotsInstanceDetails)}`;

      try {
        const simulationUrl = new URL(webotsInstanceDetails.simulationServerUrl);
        pushUnique(`${simulationUrl}${path}`);
      } catch {
        // ignore malformed URL and fall back to alternatives below
      }

      try {
        const simulationUrl = new URL(webotsInstanceDetails.simulationServerUrl);
        const webotsUrl = new URL(webotsInstanceDetails.webotsInstanceUrl);
        if (simulationUrl.port) {
          pushUnique(`${webotsUrl.protocol}//${webotsUrl.hostname}:${simulationUrl.port}${path}`);
          pushUnique(`${window.location.protocol}//${window.location.hostname}:${simulationUrl.port}${path}`);
        }
      } catch {
        // ignore malformed URL and rely on any candidate already built
      }

      return candidates;
    };

    const tryFetchStatus = async (endpoint: string) => {
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Visualization status request failed with ${response.status}`);
      }
      const payload = await response.json();
      return { payload, endpoint };
    };

    const fetchVisualizationStatus = async () => {
      const candidates = buildStatusCandidates();
      const preferredEndpoint = visualizationStatusEndpointRef.current;
      if (preferredEndpoint) {
        try {
          return await tryFetchStatus(preferredEndpoint);
        } catch {
          visualizationStatusEndpointRef.current = '';
        }
      }

      for (const endpoint of candidates) {
        try {
          return await tryFetchStatus(endpoint);
        } catch {
          // try the next candidate
        }
      }

      throw new Error('No reachable visualization status endpoint');
    };

    const refreshVisualizationStatus = async () => {
      try {
        const { payload, endpoint } = await fetchVisualizationStatus();
        if (cancelled) {
          return;
        }
        visualizationStatusEndpointRef.current = endpoint;

        const windowCount = Number(payload.window_count || 0);
        const available = Boolean(payload.enabled) && Boolean(payload.available) && windowCount > 0;

        setVisualizationAvailable((was) => {
          if (available && !was) setVisualizationUnseen(true);
          return available;
        });
        setVisualizationUrl(typeof payload.xpra_url === 'string' ? payload.xpra_url : '');
      } catch {
        if (!cancelled) {
          visualizationStatusEndpointRef.current = '';
          setVisualizationAvailable(false);
          setVisualizationUrl('');
        }
      }
    };

    refreshVisualizationStatus();
    const intervalId = window.setInterval(refreshVisualizationStatus, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [webotsInstanceDetails]);

  const retrySimulation = () => {
    // A fresh iframe announces iframe-ready again, which starts the world with a fresh token.
    setWebotsInstanceDetails(null);
    setSimulationStatus('connecting');
    setSimulationError('');
    setViewerKey((key) => key + 1);
  };

  const handleSubmit = async () => {
    if (!lesson || !canSubmit) return;

    if (!submissionWindow.open) {
      toast.error(`You can't submit this lesson: ${submissionWindow.label?.toLowerCase()}.`);
      return;
    }

    if (!hasRunCodeRef.current) {
      const submitAnyway = await confirm({
        title: "You haven't run this lesson yet",
        message: 'Use "Save & Run" to try your code in the simulation before submitting. Your professor sees the code and any metrics of the run.',
        confirmLabel: 'Submit anyway',
      });
      if (!submitAnyway) {
        return;
      }
    }

    setSubmitting(true);
    try {
      // The draft follows the submitted code, so reopening the lesson shows what was handed in.
      saveDraft();
      await submissionService.submit({
        lesson_id: lesson.id,
        code: codeRef.current,
        class_id: classIdParam,
        simulation_server_url: webotsInstanceDetails?.simulationServerUrl || null,
        simulation_server_port: webotsInstanceDetails?.webotsInstancePort
          ? parseInt(webotsInstanceDetails.webotsInstancePort, 10)
          : null,
        simulation_session_key: webotsInstanceDetails?.sessionKey || null,
      });

      toast.success('Code submitted. Your professor can now review it.');
      loadSubmissions(); // Reload submissions list
      setShowSubmissions(true);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(`Submission failed: ${getErrorMessage(error)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubmission = async (submissionId: number) => {
    const confirmed = await confirm({
      title: 'Delete this submission?',
      message: 'Your professor will no longer see it, and its share link stops working.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!confirmed) {
      return;
    }

    try {
      await submissionService.remove(submissionId);
      loadSubmissions();
      toast.success('Submission deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Failed to delete: ${getErrorMessage(error)}`);
    }
  };

  const copyShareLink = async (shareToken: string) => {
    const link = `${window.location.origin}/submission/${shareToken}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Share link copied to clipboard!');
    } catch {
      toast.error('Unable to copy. Please copy the link manually.');
    }
  };

  /**
   * Ctrl+S and Ctrl+Enter / "Save & Run". The student's draft is stored on its own (it doesn't need the
   * simulation); the file goes to the simulation when one is connected, and `run` restarts it.
   */
  const save = async ({ run }: { run: boolean }) => {
    const draftSaved = saveDraft();
    if (!lesson || !webotsInstanceDetails) {
      if (run) {
        toast.info('The simulation is not connected yet. Your code is saved; run it once the simulation is ready.');
      }
      await draftSaved;
      return;
    }

    setSaving(true);
    setOutput(run ? 'Saving file...' : 'Saving file to the simulation...');

    try {
      const baseUrl = webotsInstanceDetails.simulationServerUrl.replace(/\/$/, ''); // Remove trailing slash
      const response = await fetch(`${baseUrl}/edit-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file: lesson.editable_file_path,
          content: codeRef.current,
          port: webotsInstanceDetails.webotsInstancePort,
          ...(webotsInstanceDetails.sessionKey ? { key: webotsInstanceDetails.sessionKey } : {}),
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        setOutput('Saving to the simulation failed');
        toast.error(`Error saving the file to the simulation: ${errorText || response.status}`);
        return;
      }
      if (run) {
        hasRunCodeRef.current = true;
        // The reset pauses the world first: that pause doesn't end a run of the new code (WRK-20).
        runPlayingRef.current = false;
        setRunMetrics(null);
        setOutput('File saved. Resetting simulation...');
        postToViewer({ type: 'reload-simulation' });
        showResultsAfterRun();
      } else {
        setOutput('File saved to the simulation');
      }
    } catch (error) {
      console.error('Save error:', error);
      setOutput('Saving to the simulation failed');
      toast.error(`Error: ${getErrorMessage(error, 'Failed to save file')}`);
    } finally {
      setSaving(false);
    }
    await draftSaved;
  };

  // The keyboard handler is bound once; it calls whatever save() is current.
  const saveRef = useRef(save);
  saveRef.current = save;
  const savingRef = useRef(saving);
  savingRef.current = saving;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isModifier = event.ctrlKey || event.metaKey;
      if (!isModifier || showReportRef.current)
        return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        if (!savingRef.current)
          saveRef.current({ run: false });
      } else if (key === 'enter') {
        event.preventDefault();
        if (!savingRef.current)
          saveRef.current({ run: true });
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, []);

  const handleResetToStarter = async () => {
    if (!lesson) return;
    const confirmed = await confirm({
      title: 'Reset to the starter code?',
      message: 'Your changes in the editor are replaced by the lesson\'s starter code. Your submissions are kept.',
      confirmLabel: 'Reset code',
      danger: true,
    });
    if (!confirmed) {
      return;
    }
    const starterCode = lesson.starter_code ?? '';
    codeRef.current = starterCode;
    setCode(starterCode);
    setEditorSessionKey((currentKey) => currentKey + 1);
  };

  if (loadError) {
    return (
      <div className="page-shell flex items-center justify-center p-4">
        <div className="card p-6 sm:p-8 max-w-md w-full text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-950/50">
            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Lesson unavailable</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">{loadError}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary btn-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="page-shell">
        <LoadingState label="Loading lesson..." className="min-h-screen" />
      </div>
    );
  }

  const draftStatusLabel: Record<DraftStatus, string> = {
    idle: '',
    dirty: 'Unsaved changes',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Not saved: retrying on the next change',
  };

  // What applies to this lesson for the student, as a chip whose explanation opens on tap: practice (no class assigns
  // it) or the class's submission window.
  // The class's submission window as a chip whose explanation opens on tap (WRK-2); practice mode shows none.
  const lessonStatusChip = !isStudent || isDemo ? null : submissionWindow.label ? (
    <InfoChip icon={<CalendarClock />} label={submissionWindow.label} compactOnPhone tone={submissionWindow.open ? 'blue' : 'amber'}>
      <p className="font-semibold text-gray-900 dark:text-white mb-1">{submissionWindow.label}</p>
      {submissionWindow.open ? 'Submit before the deadline: your professor sees your latest submission.' : 'This class no longer accepts submissions for this lesson.'}
    </InfoChip>
  ) : null;

  // What a report says about the page besides the user's words (WRK-21), read when it is sent.
  const issueReportContext = (): IssueReportContext => ({
    code: codeRef.current,
    metrics: runMetrics,
    simulation_status: simulationStatus,
    simulation_error: simulationStatus === 'error' ? simulationError || null : null,
    user_agent: navigator.userAgent,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    // Wide screens always show the simulation beside the left column's pane.
    pane: isWide ? `${pane === 'simulation' ? leftPane : pane} + simulation` : pane,
  });
  const closeReport = () => {
    setShowReport(false);
    reportButtonRef.current?.focus();
  };
  // Quiet but always there on official lessons, for everyone who can open them: a flag in the top bar, labelled from sm
  // up (WRK-21). Teachers' own lessons take no reports.
  const reportButton = lesson.lesson_type === 'official' && (
    <button
      ref={reportButtonRef}
      type="button"
      onClick={() => setShowReport(true)}
      className="btn btn-ghost btn-sm shrink-0 px-2 sm:px-3"
      title="Report an issue with this lesson"
      aria-haspopup="dialog"
    >
      <Flag />
      <span className="hidden sm:inline">Report issue</span>
      <span className="sr-only sm:hidden">Report an issue</span>
    </button>
  );

  // Submitting sends the code, so its button and the history sit with Save & Run in the Code tab (WRK-2, WRK-9).
  const submitControls = canSubmit ? (
    <>
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className={`btn btn-primary btn-sm shrink-0 ${submissionWindow.open ? '' : 'opacity-70'}`}
        title={submissionWindow.open ? 'Submit your code' : submissionWindow.label || undefined}
      >
        {submitting ? <Spinner className="w-4 h-4 text-white" /> : <Send />}
        <span className="hidden sm:inline">{submitting ? 'Submitting...' : 'Submit'}</span>
        <span className="sr-only sm:hidden">Submit</span>
      </button>
      <button
        onClick={() => setShowSubmissions(!showSubmissions)}
        className="btn btn-ghost btn-sm shrink-0 px-2"
        title="My submissions"
        aria-label={`My submissions (${submissions.length})`}
      >
        <History />
        {submissions.length > 0 && (
          <span className="min-w-[1.25rem] rounded-full bg-gray-200 dark:bg-gray-700 px-1.5 text-xs font-semibold tabular-nums text-gray-700 dark:text-gray-200">
            {submissions.length}
          </span>
        )}
      </button>
    </>
  ) : null;

  // The tabs: Theory, Code and, while the student's code has windows open, Visualization; on narrow screens also the
  // Simulation, which wide screens always show on the right (docs/REQUIREMENTS.md WRK-1).
  const tabs: Array<{ id: Pane; label: string; Icon: typeof BookOpen; narrowOnly?: boolean; marked?: boolean }> = [
    ...(lesson.theory_content ? [{ id: 'theory' as const, label: 'Theory', Icon: BookOpen }] : []),
    { id: 'code', label: 'Code', Icon: Code },
    ...(visualizationAvailable ? [{ id: 'visualization' as const, label: 'Visualization', Icon: AppWindow, marked: visualizationUnseen }] : []),
    { id: 'simulation', label: 'Simulation', Icon: Cpu, narrowOnly: true },
  ];
  // Each tab's controls sit in the tab bar, on the right, while that tab is selected (WRK-1). Only Code has some.
  const tabControls: Partial<Record<Pane, React.ReactNode>> = {
    code: (
      <>
        {isStudent && draftStatus !== 'idle' && (
          <span
            className={`hidden sm:inline min-w-0 truncate text-xs ${draftStatus === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
            aria-live="polite"
          >
            {draftStatusLabel[draftStatus]}
          </span>
        )}
        <button
          onClick={handleResetToStarter}
          className="btn btn-ghost btn-sm shrink-0"
          title="Reset to starter code"
          aria-label="Reset to starter code"
        >
          <RotateCcw />
          <span className="hidden xl:inline">Reset</span>
        </button>
        {/* The span carries the tooltip: a disabled button gets no hover events in some browsers. */}
        <span className="shrink-0" title={webotsInstanceDetails ? 'Save to the simulation and restart it (Ctrl+Enter)' : 'Waiting for the simulation to connect'}>
          <button
            onClick={() => save({ run: true })}
            disabled={saving || !webotsInstanceDetails}
            className="btn btn-success btn-sm"
          >
            {saving ? <RefreshCw className="animate-spin" /> : <Play />}
            <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save & Run'}</span>
            <span className="sm:hidden" aria-hidden="true">{saving ? '…' : 'Run'}</span>
            <span className="sr-only sm:hidden">Save & Run</span>
          </button>
        </span>
        {submitControls}
      </>
    ),
  };

  // What the left column shows: the selected tab, or (a phone on the Simulation tab) the one before it.
  const shownLeft: LeftPane = pane === 'simulation' ? leftPane : pane;
  const onSimulationTab = pane === 'simulation';

  return (
    // Pinned to the viewport rather than h-dvh: phone browsers resize fixed boxes when their address bar hides or shows;
    // a sized box in the page left a dark band under it once Chrome hid its bar.
    <div className="fixed inset-0 flex min-h-0 flex-col overflow-hidden bg-gray-50 dark:bg-gray-950 transition-colors duration-200">
      <AppNavBar
        title={isStudent ? 'Student Portal' : 'Dashboard'}
        backToDashboard
        fullWidth
        subtitle={(
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate" title={lesson.title}>{lesson.title}</span>
            <span className="shrink-0"><ReadinessBadge readiness={lesson.readiness} /></span>
          </span>
        )}
        actions={<>{lessonStatusChip}{reportButton}</>}
      />

      {/* Narrow: the tabs, then the selected pane (the simulation is a tab). Wide: two columns, the tabs and their pane on
          the left, the simulation on the right. Panes stay mounted (the editor, the visualization's connection). */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className={`flex min-h-0 min-w-0 flex-col bg-white dark:bg-gray-900 lg:w-1/2 lg:flex-none lg:border-r border-gray-200 dark:border-gray-800 ${onSimulationTab ? 'flex-none' : 'flex-1'}`}>
          {/* The tabs, and the selected tab's controls on the right. On phones the tabs are icons only, to leave room. */}
          <div className="flex shrink-0 items-center border-b border-gray-200 dark:border-gray-800">
            <div className="flex min-w-0 shrink-0" role="tablist" aria-label="Lesson">
              {tabs.map(({ id, label, Icon, narrowOnly, marked }) => (
                <button
                  key={id}
                  id={`lesson-tab-${id}`}
                  role="tab"
                  aria-selected={pane === id}
                  aria-controls={`lesson-pane-${id}`}
                  onClick={() => selectPane(id)}
                  title={label}
                  className={`relative flex min-w-[2.75rem] items-center justify-center gap-1.5 px-3 sm:px-4 lg:px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${narrowOnly ? 'lg:hidden' : ''} ${pane === id
                    ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                    : id === 'code' && !codeOpened
                    ? 'border-transparent text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
                    : 'border-transparent text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="sr-only sm:not-sr-only">{label}</span>
                  {marked && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600 dark:bg-blue-400" aria-label="new" />}
                  {id === 'code' && !codeOpened && pane !== 'code' && (
                    <span className="relative flex h-2 w-2 shrink-0" aria-label="start here">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75 motion-safe:animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400" />
                    </span>
                  )}
                </button>
              ))}
            </div>
            {tabControls[pane] && (
              <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2 pl-2 pr-2 sm:pr-3">{tabControls[pane]}</div>
            )}
          </div>

          <div className={`${onSimulationTab ? 'hidden lg:flex' : 'flex'} min-h-0 flex-1 flex-col`}>
            {lesson.theory_content && (
              <div id="lesson-pane-theory" role="tabpanel" aria-labelledby="lesson-tab-theory"
                className={`${shownLeft === 'theory' ? 'block' : 'hidden'} pane-in flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 theory-scrollbar`}>
                <div className="max-w-3xl markdown-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    urlTransform={lessonUrlTransform}
                    components={{
                      img: ({ node, src, alt, ...props }) => {
                        return (
                          <img
                            src={src}
                            alt={alt || ''}
                            className="max-w-full h-auto rounded-lg shadow-md my-4"
                            {...props}
                          />
                        );
                      },
                      a: ({ node, children, ...props }) => (
                        <a
                          {...props}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                      code: ({ node, className, children, ...props }: any) => {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        // react-markdown 9 no longer says whether code is inline: fenced blocks have a
                        // language class or span lines.
                        const isBlock = Boolean(match) || String(children).includes('\n');

                        if (!isBlock) {
                          return (
                            <code
                              className="bg-gray-100 dark:bg-gray-800 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded text-sm font-mono before:content-none after:content-none"
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }

                        // Handle mermaid diagrams
                        if (language === 'mermaid') {
                          // Extract the actual text content from children
                          const chart = String(children).replace(/\n$/, '');
                          return <Mermaid chart={chart} isDarkMode={isDarkMode} />;
                        }

                        return (
                          <code
                            className={`block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto ${className || ''}`}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {lesson.theory_content}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            <div id="lesson-pane-code" role="tabpanel" aria-labelledby="lesson-tab-code" className={`${shownLeft === 'code' ? 'flex' : 'hidden'} pane-in flex-1 min-h-0 flex-col`}>
              <div className="flex-1 min-h-0 overflow-hidden">
                <Editor
                  key={`lesson-editor-${editorSessionKey}`}
                  height="100%"
                  defaultLanguage="python"
                  defaultValue={code}
                  onChange={(value) => {
                    codeRef.current = value || '';
                    setCode(value || '');
                  }}
                  theme={monacoTheme}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    padding: { top: 8, bottom: 8 },
                    scrollBeyondLastLine: false,
                    renderLineHighlight: 'all',
                    lineNumbersMinChars: 3,
                    // The pane changes size (tabs, collapsing sections), so follow the container.
                    automaticLayout: true,
                  }}
                />
              </div>
            </div>

            {visualizationAvailable && (
              <div id="lesson-pane-visualization" role="tabpanel" aria-labelledby="lesson-tab-visualization"
                className={`${shownLeft === 'visualization' ? 'block' : 'hidden'} pane-in relative flex-1 min-h-0 bg-gray-900`}>
                {visualizationUrl ? (
                  <iframe src={visualizationUrl} className="absolute inset-0 w-full h-full border-0" title="Visualization windows" />
                ) : (
                  <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-400">
                    <Spinner className="w-4 h-4 text-gray-400" />
                    Waiting for the visualization...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div id="lesson-pane-simulation" role="tabpanel" aria-labelledby="lesson-tab-simulation"
          className={`${onSimulationTab ? 'flex' : 'hidden lg:flex'} pane-in min-h-0 min-w-0 flex-1 flex-col lg:w-1/2`}>
          <div className="relative flex-1 m-0 bg-gray-950 min-h-0">
            <iframe
              key={viewerKey}
              ref={iframeRef}
              id="webots-iframe"
              src={`/webots-viewer.html?session_url=${encodeURIComponent(process.env.REACT_APP_SESSION_URL || 'http://localhost:1999')}`}
              // Pinned to the box rather than h-full: some phone browsers resolve a percentage height inside a flex
              // item to less than the box, leaving a black band under the simulation.
              className="absolute inset-0 w-full h-full border-0"
              title="Webots Simulation"
            />
            {/* No header bar over the simulation (the tabs and the page already name it): what the student needs to
                know, the connection, a save's result and the time left, is one pill over the view. */}
            {(simulationStatus === 'connecting' || (simulationStatus === 'ready' && (notice || secondsLeft !== null))) && (
              <div className="absolute top-3 left-3 right-3 flex pointer-events-none">
                <div className="flex min-w-0 items-center gap-2 rounded-full bg-gray-950/75 text-white text-xs font-medium px-3 py-1.5 backdrop-blur" role="status" aria-live="polite">
                  {simulationStatus === 'connecting' && <Spinner className="w-3.5 h-3.5 shrink-0 text-white" />}
                  {simulationStatus === 'connecting'
                    ? <span className="truncate">Connecting to the simulation…</span>
                    : notice && <span className="truncate" title={notice}>{notice}</span>}
                  {secondsLeft !== null && (
                    <>
                      {(simulationStatus === 'connecting' || notice) && <span className="text-white/40" aria-hidden="true">·</span>}
                      <span className={`shrink-0 tabular-nums ${secondsLeft < 120 ? 'text-amber-300' : 'text-white/80'}`}>
                        {secondsLeft > 0
                          ? `${formatMinutes(secondsLeft)} ${timeSource === 'class' ? 'class time ' : ''}left today`
                          : `No ${timeSource === 'class' ? 'class ' : ''}time left today`}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            {simulationStatus === 'ready' && runMetrics && (
              <RunResults metrics={runMetrics} onClose={() => setRunMetrics(null)}
                className={notice || secondsLeft !== null ? 'top-14' : 'top-3'} />
            )}
            {simulationStatus === 'time-used-up' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-950/90 p-6 backdrop-blur-sm">
                <div className="max-w-sm text-center text-gray-100">
                  <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                  {timeUsedUp.source === 'class' ? (
                    <>
                      <p className="font-semibold text-lg mb-1">You've used today's time for this class</p>
                      <p className="text-sm text-gray-300 mb-4">
                        This class's Class plan gives each student {formatDailyTime(timeUsedUp.limitSeconds ?? 3600)} of simulation a day for its lessons.
                        {timeResetsAt && <> More time {formatResetTime(timeResetsAt)}.</>}
                      </p>
                    </>
                  ) : timeUsedUp.plan === 'student' ? (
                    <>
                      <p className="font-semibold text-lg mb-1">You've used today's simulation time</p>
                      <p className="text-sm text-gray-300 mb-4">Your Student plan includes {formatDailyTime(timeUsedUp.limitSeconds ?? 3600)} a day.{timeResetsAt && <> More time {formatResetTime(timeResetsAt)}.</>}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-lg mb-1">You've used today's free simulation time</p>
                      <p className="text-sm text-gray-300 mb-4">
                        Free accounts can try lessons for 10 minutes a day.
                        {timeResetsAt && <> More time {formatResetTime(timeResetsAt)}.</>}
                      </p>
                      <UpgradeButton
                        plan="student"
                        className="btn btn-primary btn-lg mb-3"
                        // PLAN-18: until online payment is on, a free month of the Student plan instead.
                        trial={{
                          className: 'mb-3',
                          offer: <span className="font-semibold text-gray-100">Student plan · €9/month</span>,
                          noteClassName: 'text-sm text-gray-300',
                          buttonClassName: 'btn btn-primary btn-lg',
                        }}
                      >
                        Get the Student plan · €9/month
                      </UpgradeButton>
                      <p className="text-sm text-gray-300">1 hour of simulation a day. In a course? Your professor's Class plan gives you time for its lessons.</p>
                      <PaymentsNote className="mt-2 text-xs text-gray-400" />
                    </>
                  )}
                  <p className="text-xs text-gray-400 mt-4">You can keep reading and editing: your code is saved as you type.</p>
                </div>
              </div>
            )}
            {simulationStatus === 'error' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-950/90 p-6 backdrop-blur-sm">
                <div className="max-w-sm text-center text-gray-100">
                  <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                  <p className="font-semibold text-lg mb-1">Simulation unavailable</p>
                  <p className="text-sm text-gray-300 mb-5 break-words">{simulationError}</p>
                  <button
                    onClick={retrySimulation}
                    className="btn btn-primary btn-lg"
                  >
                    <RefreshCw />
                    Retry
                  </button>
                  <p className="text-xs text-gray-400 mt-4">You can keep editing: your code is saved as you type.</p>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {showReport && (
        <ReportIssueDialog lessonId={lesson.id} lessonTitle={lesson.title} getContext={issueReportContext} onClose={closeReport} />
      )}

      {/* Submissions Modal */}
      {showSubmissions && (
        <div className="modal-backdrop p-2 sm:p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="my-submissions-title" className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-800">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center gap-3">
              <div className="min-w-0">
                <h3 id="my-submissions-title" className="modal-title">My Submissions</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{lesson.title}</p>
              </div>
              <button
                onClick={() => setShowSubmissions(false)}
                className="btn btn-ghost btn-icon shrink-0 [&_svg]:w-5 [&_svg]:h-5"
                aria-label="Close"
              >
                <X />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-950/40">
              {submissions.length === 0 ? (
                <EmptyState icon={<History />} title="No submissions yet" className="bg-white dark:bg-gray-900">
                  Click "Submit" to submit your code.
                </EmptyState>
              ) : (
                <div className="space-y-3">
                  {submissions.map((submission) => (
                    <div key={submission.id} className="card p-4 sm:p-5 flex flex-col">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <StatusBadge status={submission.status} size="sm" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                          {formatDateTime(submission.submitted_at)}
                        </span>
                        <button
                          onClick={() => handleDeleteSubmission(submission.id)}
                          className="ml-auto btn btn-ghost-danger btn-sm"
                          title="Delete submission"
                          aria-label="Delete submission"
                        >
                          <Trash2 />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                      {submission.teacher_review && (
                        <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900/60 rounded-xl p-4 mb-3">
                          <p className="text-sm font-semibold text-violet-900 dark:text-violet-200 mb-1.5 flex items-center gap-2">
                            <GraduationCap className="w-4 h-4" /> Teacher Review
                          </p>
                          <p className="text-sm text-violet-900/90 dark:text-violet-100 leading-relaxed whitespace-pre-wrap">{submission.teacher_review}</p>
                          {submission.reviewed_at && (
                            <p className="text-xs text-violet-700 dark:text-violet-300 mt-2">
                              Reviewed on {formatDateTime(submission.reviewed_at)}
                            </p>
                          )}
                        </div>
                      )}
                      {submission.metrics && typeof submission.metrics === 'object' && Object.keys(submission.metrics).length > 0 && (
                        <MetricsDisplay metrics={submission.metrics} title="Simulation Metrics" className="mb-3" />
                      )}
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 pl-3 pr-1 py-1">
                        <span className="eyebrow whitespace-nowrap hidden sm:inline">Share link</span>
                        <code className="flex-1 min-w-0 text-xs text-gray-700 dark:text-gray-300 truncate font-mono">
                          {`${window.location.origin}/submission/${submission.share_token}`}
                        </code>
                        <button
                          onClick={() => copyShareLink(submission.share_token)}
                          className="btn btn-secondary btn-sm shrink-0"
                          title="Copy to clipboard"
                        >
                          <Copy />
                          Copy Link
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonPage;
